import React, { useState, useEffect } from 'react';
import AdminNavbar from '../components/AdminNavbar';
import { PencilIcon, TrashIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { userService } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const LegalProfessionalsPage = () => {
    const [legalProfessionals, setLegalProfessionals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { isAdmin } = useAuth();
    
    // Form validation errors
    const [validationErrors, setValidationErrors] = useState({});
    
    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    
    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        role_name: 'legal_professional',
        name: '',
        image_url: ''
    });

    const navigate = useNavigate();

    // For organization selection modal
    const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [orgSearchTerm, setOrgSearchTerm] = useState('');
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [loadingOrgs, setLoadingOrgs] = useState(false);

    // Debounce search term
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        
        return () => {
            clearTimeout(timerId);
        };
    }, [searchTerm]);

    // Load legal professionals when search changes
    useEffect(() => {
        // Only fetch if debounced search term is defined
        if (debouncedSearchTerm !== undefined) {
            // Reset to page 1 when search term changes
            setPagination(prev => ({
                ...prev,
                page: 1
            }));
            fetchLegalProfessionals();
        }
    }, [debouncedSearchTerm]);

    // Load when pagination parameters change
    useEffect(() => {
        fetchLegalProfessionals();
    }, [pagination.page, pagination.limit]);

    const fetchLegalProfessionals = async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            // Log the search parameters for debugging
            console.log('Fetching legal professionals with search:', debouncedSearchTerm);
            
            // Use the dedicated endpoint for legal professionals
            const options = {
                page: pagination.page,
                limit: pagination.limit,
                search: debouncedSearchTerm || '' // Ensure we send empty string if undefined
            };
            
            console.log('Fetch options:', options);
            const data = await userService.getLegalProfessionals(options);
            console.log('Received data from API:', data);
            
            // Make sure data.users exists
            if (!data || !data.users) {
                console.error('Invalid data format:', data);
                throw new Error('Invalid data format returned from API');
            }
            
            setLegalProfessionals(data.users);
            setPagination({
                page: data.pagination?.page || 1,
                limit: data.pagination?.limit || 10,
                total: data.pagination?.total || 0,
                totalPages: data.pagination?.totalPages || 1
            });
        } catch (err) {
            console.error('Error fetching legal professionals:', err);
            
            // Get a more specific error message
            let errorMessage;
            try {
                if (err.response?.data?.error) {
                    errorMessage = err.response.data.error;
                } else if (err.message) {
                    errorMessage = err.message;
                } else {
                    errorMessage = 'Failed to load legal professionals. Please try again later.';
                }
            } catch (errorHandlingError) {
                errorMessage = 'Failed to load legal professionals. Please try again later.';
            }
            
            setError(errorMessage);
            toast.error(errorMessage);
            
            // Set empty data to prevent UI crashes, but keep pagination state
            setLegalProfessionals([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        // No need to reset pagination here, it will be done in the effect
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination({
                ...pagination,
                page: newPage
            });
        }
    };

    // Handle items per page change
    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setPagination({
            ...pagination,
            page: 1, // Reset to first page when changing limit
            limit: newLimit
        });
    };

    const handleAddUser = () => {
        setEditingUser(null);
        setFormData({
            username: '',
            email: '',
            password: '',
            role_name: 'legal_professional', // Default to legal_professional
            name: '',
            image_url: ''
        });
        setValidationErrors({});
        setIsModalOpen(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setFormData({
            username: user.username,
            email: user.email,
            password: '', // Don't include password when editing
            role_name: user.role_name,
            name: user.name || '',
            image_url: user.image_url || ''
        });
        setValidationErrors({});
        setIsModalOpen(true);
    };

    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const validateForm = () => {
        const errors = {};
        
        // Username validation
        if (!formData.username || formData.username.trim() === '') {
            errors.username = 'Username is required';
        } else if (formData.username.length < 3) {
            errors.username = 'Username must be at least 3 characters';
        } else if (formData.username.length > 50) {
            errors.username = 'Username must be less than 50 characters';
        }
        
        // Email validation
        if (!formData.email || formData.email.trim() === '') {
            errors.email = 'Email is required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
            errors.email = 'Invalid email format';
        }
        
        // Password validation (only for new users)
        if (!editingUser && !formData.password) {
            errors.password = 'Password is required for new users';
        } else if (formData.password && formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }
        
        // Name validation
        if (formData.name && formData.name.length > 100) {
            errors.name = 'Name must be less than 100 characters';
        }
        
        // Role is always legal_professional so no validation needed
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear validation error when field is edited
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        try {
            setIsSubmitting(true);
            
            // Ensure role is set to legal_professional
            const userData = {
                ...formData,
                role_name: 'legal_professional'
            };
            
            if (editingUser) {
                // Remove password if not provided
                if (!userData.password) {
                    delete userData.password;
                }
                
                await userService.updateUser(editingUser.id, userData);
                toast.success('Legal professional updated successfully');
            } else {
                await userService.createUser(userData);
                toast.success('Legal professional added successfully');
            }
            
            setIsModalOpen(false);
            fetchLegalProfessionals();
        } catch (err) {
            console.error('Operation failed:', err);
            
            // Handle different types of errors
            if (err.response?.status === 400) {
                // Validation errors
                if (err.response.data?.errors) {
                    setValidationErrors(err.response.data.errors);
                    toast.error('Please fix the validation errors');
                } else {
                    toast.error(err.response.data?.error || 'Validation failed');
                }
            } else if (err.response?.status === 409) {
                // Conflict error (duplicate email/username)
                const field = err.response.data?.field || '';
                if (field === 'username') {
                    setValidationErrors({
                        ...validationErrors,
                        username: 'This username is already taken'
                    });
                } else if (field === 'email') {
                    setValidationErrors({
                        ...validationErrors,
                        email: 'This email is already registered'
                    });
                } else {
                    toast.error(err.response.data?.error || 'A user with this information already exists');
                }
            } else if (err.response?.status === 403) {
                // Permission error
                toast.error(err.response.data?.error || 'You do not have permission to perform this action');
            } else {
                // Server or network error
                toast.error(err.response.data?.error || 'Operation failed. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsSubmitting(true);
            await userService.deleteUser(selectedUser.id);
            toast.success('Legal professional deleted successfully');
            setIsDeleteModalOpen(false);
            fetchLegalProfessionals();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to delete legal professional');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // New function to navigate to the claimed profile
    const handleGoToProfile = (userId) => {
        try {
            // Create a more detailed alert for users
            console.log(`Navigating to lawyer profile with ID: ${userId}`);
            
            // Add a custom event to track this navigation
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'navigate_to_lawyer_profile',
                    lawyerId: userId
                });
            }
            
            // Navigate directly to the lawyer profile without API call
            navigate(`/solicitor/${userId}`);
        } catch (error) {
            console.error('Error navigating to lawyer profile:', error);
            toast.error('Error opening lawyer profile. Please try again later.');
        }
    };

    // Load organizations for the modal
    const fetchOrganizations = async (searchTerm = '') => {
        try {
            setLoadingOrgs(true);
            const response = await userService.getOrganizations(searchTerm);
            setOrganizations(response);
        } catch (error) {
            console.error('Error fetching organizations:', error);
            toast.error('Failed to load organizations');
        } finally {
            setLoadingOrgs(false);
        }
    };

    // Open the claim profile modal
    const handleManageClaimedProfile = async (user) => {
        setSelectedUser(user);
        setOrgSearchTerm('');
        setSelectedOrg(null);
        
        // If the user already has a claimed profile, fetch the organization details
        if (user.is_claimed) {
            try {
                const claimedProfile = await userService.getClaimedProfile(user.id);
                console.log('Claimed profile:', claimedProfile);
                
                // If we have organization data, pre-select it
                if (claimedProfile && claimedProfile.organisation_id) {
                    setSelectedOrg({
                        id: claimedProfile.organisation_id,
                        practice_name: claimedProfile.practice_name,
                        sra_number: claimedProfile.sra_number
                    });
                }
            } catch (error) {
                console.error('Error fetching claimed profile:', error);
                // Continue without pre-selected org if there's an error
            }
        }
        
        // Load initial list of organizations
        fetchOrganizations();
        
        // Open the modal
        setIsOrgModalOpen(true);
    };

    // Handle organization search
    const handleOrgSearch = (e) => {
        const searchTerm = e.target.value;
        setOrgSearchTerm(searchTerm);
        
        // Debounce search to avoid too many requests
        const timerId = setTimeout(() => {
            fetchOrganizations(searchTerm);
        }, 500);
        
        return () => clearTimeout(timerId);
    };

    // Select an organization from the list
    const handleSelectOrg = (org) => {
        setSelectedOrg(org);
    };

    // Set the claimed profile status
    const handleSetClaimedProfile = async (isClaimed) => {
        try {
            setIsSubmitting(true);
            
            if (isClaimed && !selectedOrg) {
                toast.error('Please select an organisation first');
                return;
            }
            
            // Ensure we're passing a number for the organisation_id
            const payload = {
                is_claimed: isClaimed,
                organisation_id: isClaimed ? parseInt(selectedOrg.id, 10) : null
            };
            
            console.log('Sending claim profile payload:', payload);
            
            const response = await userService.setClaimedProfile(selectedUser.id, payload);
            
            console.log('Claim profile response:', response);
            
            toast.success(`Profile ${isClaimed ? 'claimed' : 'unclaimed'} successfully`);
            setIsOrgModalOpen(false);
            
            // Refresh the list
            fetchLegalProfessionals();
        } catch (error) {
            console.error('Error setting claimed profile:', error);
            
            // Get detailed error message from response if available
            let errorMessage = 'Failed to update claimed profile status';
            
            if (error.response) {
                console.log('Error response:', error.response.data);
                if (error.response.data.details) {
                    errorMessage += `: ${error.response.data.details}`;
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                }
            }
            
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Organization selection modal
    const renderOrgModal = () => {
        if (!isOrgModalOpen || !selectedUser) return null;
        
        return (
            <div className="fixed z-50 inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto">
                <div className="bg-white rounded-lg w-full max-w-md mx-auto my-8 overflow-hidden shadow-xl">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Manage Claimed Profile
                        </h3>
                        <button
                            onClick={() => setIsOrgModalOpen(false)}
                            className="text-gray-400 hover:text-gray-500 transition-colors"
                            aria-label="Close"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                    
                    <div className="p-4 sm:p-6">
                        <div className="mb-4">
                            <p className="text-gray-700">
                                User: <span className="font-semibold">{selectedUser.username}</span> ({selectedUser.email})
                            </p>
                            <p className="text-gray-700 mt-1">
                                Current Status: 
                                <span className={selectedUser.is_claimed ? "text-green-600 font-medium ml-2" : "text-red-500 font-medium ml-2"}>
                                    {selectedUser.is_claimed ? "Claimed" : "Not Claimed"}
                                </span>
                            </p>
                        </div>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Search Organisations
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search by name or SRA number..."
                                    value={orgSearchTerm}
                                    onChange={handleOrgSearch}
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                />
                                {loadingOrgs && (
                                    <div className="absolute right-2 top-2">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700"></div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="mt-3 mb-4 border border-gray-200 rounded-md overflow-y-auto max-h-60">
                            {organizations.length === 0 ? (
                                <p className="text-gray-500 p-3 text-center">
                                    {orgSearchTerm ? 'No organizations found' : 'Enter search terms to find organizations'}
                                </p>
                            ) : (
                                <ul className="divide-y divide-gray-200">
                                    {organizations.map(org => (
                                        <li 
                                            key={org.id}
                                            className={`p-3 cursor-pointer hover:bg-gray-50 ${selectedOrg?.id === org.id ? 'bg-indigo-50' : ''}`}
                                            onClick={() => handleSelectOrg(org)}
                                        >
                                            <div className="flex justify-between">
                                                <div>
                                                    <p className="font-medium text-gray-900">{org.practice_name}</p>
                                                    <p className="text-sm text-gray-500">SRA: {org.sra_number || 'N/A'}</p>
                                                </div>
                                                {selectedOrg?.id === org.id && (
                                                    <div className="flex items-center text-indigo-600">
                                                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        
                        <div className="mt-5 border-t pt-4 flex justify-between">
                            <button
                                onClick={() => handleSetClaimedProfile(false)}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                                disabled={isSubmitting || !selectedUser.is_claimed}
                            >
                                {isSubmitting ? 'Updating...' : 'Remove Claimed Status'}
                            </button>
                            
                            <button
                                onClick={() => handleSetClaimedProfile(true)}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                                disabled={isSubmitting || !selectedOrg}
                            >
                                {isSubmitting ? 'Updating...' : (selectedUser.is_claimed ? 'Update Organisation' : 'Set as Claimed')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render pagination controls with improved error handling
    const renderPagination = () => {
        const { page, totalPages, total } = pagination;
        
        // Don't render pagination if there's no data or only one page
        if (totalPages <= 1 || total === 0) return null;
        
        // Create page numbers array with ellipsis for large page counts
        const getPageNumbers = () => {
            try {
                const pages = [];
                const maxVisiblePages = 5;
                
                if (totalPages <= maxVisiblePages) {
                    // Show all pages if there are few
                    for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                    }
                } else {
                    // Always include first page
                    pages.push(1);
                    
                    // Calculate start and end of visible pages around current page
                    let start = Math.max(2, page - 1);
                    let end = Math.min(totalPages - 1, page + 1);
                    
                    // Add ellipsis if needed before start
                    if (start > 2) {
                        pages.push('...');
                    }
                    
                    // Add visible pages
                    for (let i = start; i <= end; i++) {
                        pages.push(i);
                    }
                    
                    // Add ellipsis if needed after end
                    if (end < totalPages - 1) {
                        pages.push('...');
                    }
                    
                    // Always include last page
                    pages.push(totalPages);
                }
                
                return pages;
            } catch (error) {
                console.error("Error in pagination rendering:", error);
                return [1]; // Safe fallback
            }
        };
        
        // Safely get page numbers
        const pageNumbers = getPageNumbers();
        
        return (
            <div className="flex items-center justify-between px-4 py-3 mt-4">
                <div className="flex flex-1 justify-between sm:hidden">
                    <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className={`relative inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium ${
                            page === 1
                                ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        className={`relative inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium ${
                            page === totalPages
                                ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        Next
                    </button>
                </div>
                
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Showing {total === 0 ? '0' : 
                                <span>
                                    <span className="font-medium">{Math.min((page - 1) * pagination.limit + 1, total)}</span> to{' '}
                                    <span className="font-medium">{Math.min(page * pagination.limit, total)}</span>
                                </span>
                            } of <span className="font-medium">{total}</span> results
                        </p>
                    </div>
                    <div>
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-700">Rows per page:</span>
                            <select
                                className="rounded border-gray-300 text-sm"
                                value={pagination.limit}
                                onChange={handleLimitChange}
                                disabled={total === 0}
                            >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                    </div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1 || total === 0}
                            className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                                page === 1 || total === 0
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-500 hover:bg-gray-50'
                                }`}
                        >
                            <span className="sr-only">Previous</span>
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                        
                        {pageNumbers.map((pageNum, i) => (
                            pageNum === '...' ? (
                                <span 
                                    key={`ellipsis-${i}`} 
                                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white"
                                >
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                        pageNum === page
                                            ? 'bg-indigo-600 text-white focus:z-20'
                                            : 'bg-white text-gray-900 hover:bg-gray-50'
                                        }`}
                                >
                                    {pageNum}
                                </button>
                            )
                        ))}
                        
                        <button
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page === totalPages || total === 0}
                            className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                                page === totalPages || total === 0
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-500 hover:bg-gray-50'
                                }`}
                        >
                            <span className="sr-only">Next</span>
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </nav>
                </div>
            </div>
        );
    };

    // Add a Retry button to reload legal professionals
    const handleRetryFetch = () => {
        fetchLegalProfessionals();
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-100">
                <AdminNavbar />
                <div className="container mx-auto px-4 py-8">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Manage Legal Professionals</h1>
                        <button 
                            onClick={handleAddUser}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
                        >
                            Add Legal Professional
                        </button>
                    </div>
                    
                    <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                        <div className="px-4 py-5 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                Legal Professionals List
                            </h3>
                            <div className="relative w-64">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder="Search legal professionals..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                                {searchTerm && (
                                    <button 
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <XMarkIcon className="h-5 w-5 text-gray-400" />
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        {/* Loading and Error State */}
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                        </div>
                        
                        {error && !isLoading && (
                            <div className="flex flex-col justify-center items-center h-64">
                                <div className="text-red-500 text-center mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-lg font-semibold">{error}</p>
                                </div>
                                <button 
                                    onClick={handleRetryFetch}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
                                >
                                    Retry
                                </button>
                            </div>
                        )}
                        
                        {!isLoading && !error && (
                            <div className="overflow-x-auto">
                                {legalProfessionals.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">No legal professionals found</p>
                                    </div>
                                ) : (
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Username
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Name
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Claim
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {legalProfessionals.map((user) => (
                                                <tr key={user.id}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        <div className="flex items-center">
                                                            {user.username}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {user.name || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {user.email}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                            {user.status || 'Active'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {user.is_claimed && (
                                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                                Claimed
                                                            </span>
                                                        )}
                                                        {!user.is_claimed && (
                                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                                                Not Claimed
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <div className="flex justify-end items-center">
                                                            <button
                                                                onClick={() => handleManageClaimedProfile(user)}
                                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                                title="Manage claimed profile"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => handleGoToProfile(user.id)}
                                                                className="text-green-600 hover:text-green-900 mr-3"
                                                                title="View profile"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => handleEditUser(user)}
                                                                className="text-indigo-600 hover:text-indigo-900 mr-3"
                                                                title="Edit user"
                                                            >
                                                                <PencilIcon className="h-5 w-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(user)}
                                                                className="text-red-600 hover:text-red-900"
                                                                title="Delete user"
                                                            >
                                                                <TrashIcon className="h-5 w-5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}
                    </div>

                    {renderPagination()}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <AdminNavbar />
            <div className="py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-2xl font-bold mb-6">Legal Professionals</h1>

                    {Object.keys(validationErrors).length > 0 && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">
                                        There {Object.keys(validationErrors).length === 1 ? 'is an error' : 'are errors'} with your submission
                                    </h3>
                                    <div className="mt-2 text-sm text-red-700">
                                        <ul className="list-disc pl-5 space-y-1">
                                            {Object.values(validationErrors).map((error, index) => (
                                                <li key={index}>{error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mb-6 flex flex-col md:flex-row justify-between items-center">
                        <button
                            onClick={handleAddUser}
                            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors mb-4 md:mb-0"
                        >
                            Add Legal Professional
                        </button>
                        
                        <div className="relative w-full md:w-auto md:min-w-[300px]">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search legal professionals..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6">
                            {error}
                        </div>
                    )}

                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Username
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Claimed Profile
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {legalProfessionals.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                                            {debouncedSearchTerm ? 'No legal professionals found matching your search.' : 'No legal professionals found.'}
                                        </td>
                                    </tr>
                                ) : (
                                    legalProfessionals.map(user => (
                                        <tr key={user.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {user.username}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {user.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {user.name || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {user.is_claimed ? (
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center">
                                                            <span className="text-green-600 font-medium mr-2">Yes</span>
                                                            {user.claimed_organisation && (
                                                                <span className="text-gray-700 text-xs bg-gray-100 px-2 py-1 rounded-full ml-1">
                                                                    {user.claimed_organisation.practice_name}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex space-x-2 mt-2">
                                                            <button 
                                                                onClick={() => handleGoToProfile(user.id)}
                                                                className="bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 transition-colors text-xs"
                                                            >
                                                                Go to Profile
                                                            </button>
                                                            <button
                                                                onClick={() => handleManageClaimedProfile(user)}
                                                                className="bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700 transition-colors text-xs"
                                                            >
                                                                Manage
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center">
                                                        <span className="text-red-500 mr-2">No</span>
                                                        <button
                                                            onClick={() => handleManageClaimedProfile(user)}
                                                            className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition-colors"
                                                        >
                                                            Assign Profile
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button
                                                    onClick={() => handleEditUser(user)}
                                                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteClick(user)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {renderPagination()}
                </div>

                {/* Modal for adding/editing legal professionals */}
                {isModalOpen && (
                    <div className="fixed z-50 inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto">
                        <div className="bg-white rounded-lg w-full max-w-md mx-auto my-8 overflow-hidden shadow-xl">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {editingUser ? 'Edit Legal Professional' : 'Add Legal Professional'}
                                </h3>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-500 transition-colors"
                                    aria-label="Close"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="username">
                                            Username
                                        </label>
                                        <input
                                            type="text"
                                            id="username"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleInputChange}
                                            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:border-indigo-500 ${
                                                validationErrors.username ? 'border-red-500 focus:ring-red-500' : 'focus:ring-indigo-500'
                                            }`}
                                            required
                                        />
                                        {validationErrors.username && (
                                            <p className="text-red-500 text-xs mt-1 flex items-center">
                                                <svg className="h-3 w-3 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                {validationErrors.username}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:border-indigo-500 ${
                                                validationErrors.email ? 'border-red-500 focus:ring-red-500' : 'focus:ring-indigo-500'
                                            }`}
                                            required
                                        />
                                        {validationErrors.email && (
                                            <p className="text-red-500 text-xs mt-1 flex items-center">
                                                <svg className="h-3 w-3 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                {validationErrors.email}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="password">
                                            Password {editingUser && '(Leave blank to keep unchanged)'}
                                        </label>
                                        <input
                                            type="password"
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:border-indigo-500 ${
                                                validationErrors.password ? 'border-red-500 focus:ring-red-500' : 'focus:ring-indigo-500'
                                            }`}
                                            required={!editingUser}
                                        />
                                        {validationErrors.password && (
                                            <p className="text-red-500 text-xs mt-1 flex items-center">
                                                <svg className="h-3 w-3 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                {validationErrors.password}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="name">
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:border-indigo-500 ${
                                                validationErrors.name ? 'border-red-500 focus:ring-red-500' : 'focus:ring-indigo-500'
                                            }`}
                                        />
                                        {validationErrors.name && (
                                            <p className="text-red-500 text-xs mt-1 flex items-center">
                                                <svg className="h-3 w-3 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                {validationErrors.name}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="image_url">
                                            Profile Image URL
                                        </label>
                                        <input
                                            type="text"
                                            id="image_url"
                                            name="image_url"
                                            value={formData.image_url}
                                            onChange={handleInputChange}
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-500"
                                            placeholder="https://example.com/image.jpg"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enter a URL to a profile image (optional)
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-6 border-t pt-4 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Saving...' : (editingUser ? 'Update' : 'Create')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Delete confirmation modal */}
                {isDeleteModalOpen && selectedUser && (
                    <div className="fixed z-50 inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto">
                        <div className="bg-white rounded-lg w-full max-w-sm mx-auto my-8 overflow-hidden shadow-xl">
                            <div className="p-4 sm:p-6">
                                <div className="flex items-center justify-between mb-4 border-b pb-3">
                                    <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                                    <button
                                        onClick={() => setIsDeleteModalOpen(false)}
                                        className="text-gray-400 hover:text-gray-500 transition-colors"
                                        aria-label="Close"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>
                                
                                <p className="mb-6 text-gray-700">
                                    Are you sure you want to delete user <span className="font-medium">{selectedUser.username}</span>? This action cannot be undone.
                                </p>
                                
                                <div className="flex justify-end space-x-3">
                                    <button
                                        onClick={() => setIsDeleteModalOpen(false)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDeleteConfirm}
                                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add the organization selection modal */}
                {renderOrgModal()}
            </div>
        </div>
    );
};

export default LegalProfessionalsPage;
